import { useMemo, useSyncExternalStore } from "react";

export type FloatingWidgetMetrics = {
  bottom: number;
  height: number;
  isCompact: boolean;
  right: number;
  visible: boolean;
  width: number;
};

export type FloatingWidgetClearance = {
  bottomOffset: number;
  contentBottomPadding: number;
  rightOffset: number;
};

export type ManagedFloatingWidgetId =
  | "front-chat"
  | "agent-fab"
  | "debug-clipboard"
  | "super-admin-hints"
  | "agent-chat-minimized"
  | "scroll-to-top";

type FloatingWidgetPlacement = {
  bottom: number;
  right: number;
};

export type FloatingWidgetViewportInput = {
  innerHeight: number;
  innerWidth: number;
  isCoarsePointer?: boolean;
  visualViewportHeight?: number;
  visualViewportOffsetTop?: number;
};

type FloatingWidgetLayoutOptions = {
  viewportOffset?: FloatingWidgetPlacement;
};

export type FloatingWidgetSlotSize = {
  height: number;
  width: number;
};

const SENTRY_WIDGET_ID = "sentry-feedback";
const COMPACT_TRIGGER_WIDTH = 96;
const DEFAULT_WIDGET_MARGIN = 16;
const FLOATING_CONTROL_ROW_GAP = 12;
const FLOATING_CONTROL_STACK_GAP = 12;
const FLOATING_CONTROL_CLEARANCE_GAP = 12;
const MOBILE_BROWSER_CHROME_MIN_BOTTOM_OFFSET = 96;
const MOBILE_FLOATING_WIDGET_MAX_WIDTH = 640;
const COARSE_POINTER_QUERY = "(pointer: coarse)";

const MANAGED_WIDGET_ORDER: ManagedFloatingWidgetId[] = [
  "front-chat",
  "agent-fab",
  "debug-clipboard",
  "agent-chat-minimized",
  // ENT-5840: after the minimized SupplySteward panel, so the switch never lands on top of it.
  "super-admin-hints",
  "scroll-to-top",
];

const MANAGED_WIDGET_SIZES: Record<ManagedFloatingWidgetId, FloatingWidgetSlotSize> = {
  "front-chat": { width: 40, height: 40 },
  "agent-fab": { width: 40, height: 40 },
  "debug-clipboard": { width: 40, height: 40 },
  "super-admin-hints": { width: 40, height: 40 },
  "agent-chat-minimized": { width: 224, height: 48 },
  "scroll-to-top": { width: 48, height: 48 },
};

const widgetMetrics = new Map<string, FloatingWidgetMetrics>();
const listeners = new Set<() => void>();

function emitChange() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getDefaultViewportOffset(): FloatingWidgetPlacement {
  return {
    bottom: DEFAULT_WIDGET_MARGIN,
    right: DEFAULT_WIDGET_MARGIN,
  };
}

export function getFloatingWidgetViewportOffset(
  viewport: FloatingWidgetViewportInput,
): FloatingWidgetPlacement {
  const visualViewportBottomInset = Math.max(
    0,
    viewport.innerHeight
      - ((viewport.visualViewportOffsetTop ?? 0) + (viewport.visualViewportHeight ?? viewport.innerHeight)),
  );
  const bottom = DEFAULT_WIDGET_MARGIN + visualViewportBottomInset;
  const shouldClearMobileBrowserChrome =
    viewport.innerWidth <= MOBILE_FLOATING_WIDGET_MAX_WIDTH || Boolean(viewport.isCoarsePointer);

  return {
    bottom: shouldClearMobileBrowserChrome
      ? Math.max(bottom, MOBILE_BROWSER_CHROME_MIN_BOTTOM_OFFSET)
      : bottom,
    right: DEFAULT_WIDGET_MARGIN,
  };
}

function getViewportInput(): FloatingWidgetViewportInput | null {
  if (typeof window === "undefined") {
    return null;
  }

  const visualViewport = window.visualViewport;

  return {
    innerHeight: window.innerHeight,
    innerWidth: window.innerWidth,
    isCoarsePointer: window.matchMedia?.(COARSE_POINTER_QUERY).matches ?? false,
    visualViewportHeight: visualViewport?.height,
    visualViewportOffsetTop: visualViewport?.offsetTop,
  };
}

export function getCurrentFloatingWidgetViewportOffset(): FloatingWidgetPlacement {
  const viewportInput = getViewportInput();

  return viewportInput ? getFloatingWidgetViewportOffset(viewportInput) : getDefaultViewportOffset();
}

function getFloatingWidgetViewportOffsetSnapshot() {
  const { bottom, right } = getCurrentFloatingWidgetViewportOffset();

  return `${right}:${bottom}`;
}

function subscribeToViewportOffset(listener: () => void) {
  if (typeof window === "undefined") {
    return () => {};
  }

  const visualViewport = window.visualViewport;
  const pointerQuery = window.matchMedia?.(COARSE_POINTER_QUERY);
  window.addEventListener("resize", listener);
  visualViewport?.addEventListener("resize", listener);
  visualViewport?.addEventListener("scroll", listener);
  if (pointerQuery?.addEventListener) {
    pointerQuery.addEventListener("change", listener);
  } else {
    pointerQuery?.addListener?.(listener);
  }

  return () => {
    window.removeEventListener("resize", listener);
    visualViewport?.removeEventListener("resize", listener);
    visualViewport?.removeEventListener("scroll", listener);
    if (pointerQuery?.removeEventListener) {
      pointerQuery.removeEventListener("change", listener);
    } else {
      pointerQuery?.removeListener?.(listener);
    }
  };
}

function useFloatingWidgetViewportOffset(): FloatingWidgetPlacement {
  const snapshot = useSyncExternalStore(
    subscribeToViewportOffset,
    getFloatingWidgetViewportOffsetSnapshot,
    () => `${DEFAULT_WIDGET_MARGIN}:${DEFAULT_WIDGET_MARGIN}`,
  );

  return useMemo(() => {
    const [right, bottom] = snapshot.split(":").map(Number);

    return {
      bottom,
      right,
    };
  }, [snapshot]);
}

function normalizeMetrics(metrics: {
  bottom: number;
  height: number;
  right: number;
  visible: boolean;
  width: number;
}): FloatingWidgetMetrics {
  return {
    ...metrics,
    isCompact: metrics.width <= COMPACT_TRIGGER_WIDTH,
  };
}

function getWidgetMetrics(widgetId: string) {
  return widgetMetrics.get(widgetId) ?? null;
}

function getManagedWidgetSize(widgetId: ManagedFloatingWidgetId) {
  return MANAGED_WIDGET_SIZES[widgetId];
}

export function getManagedFloatingWidgetSlotSize(widgetId: ManagedFloatingWidgetId): FloatingWidgetSlotSize {
  return getManagedWidgetSize(widgetId);
}

function usesCompactCircularLane(
  anchorMetrics: FloatingWidgetMetrics | null,
  widgetIds: ManagedFloatingWidgetId[],
) {
  return Boolean(anchorMetrics?.isCompact)
    && widgetIds.every((widgetId) => getManagedWidgetSize(widgetId).width <= 50);
}

function getLaneMode(anchorMetrics: FloatingWidgetMetrics | null, widgetIds: ManagedFloatingWidgetId[]) {
  if (!anchorMetrics?.visible) {
    return "stack";
  }

  if (anchorMetrics.isCompact && !usesCompactCircularLane(anchorMetrics, widgetIds)) {
    return "stack";
  }

  return "row";
}

function getVisibleManagedWidgetIds(widgetIds: ManagedFloatingWidgetId[]) {
  return MANAGED_WIDGET_ORDER.filter((widgetId) => widgetIds.includes(widgetId));
}

function getManagedWidgetPlacements(
  anchorMetrics: FloatingWidgetMetrics | null,
  widgetIds: ManagedFloatingWidgetId[],
  options: FloatingWidgetLayoutOptions = {},
) {
  const placements = new Map<ManagedFloatingWidgetId, FloatingWidgetPlacement>();
  const visibleWidgetIds = getVisibleManagedWidgetIds(widgetIds);
  const viewportOffset = options.viewportOffset ?? getDefaultViewportOffset();

  if (visibleWidgetIds.length === 0) {
    return placements;
  }

  const laneMode = getLaneMode(anchorMetrics, visibleWidgetIds);
  let previousRight = anchorMetrics?.visible ? anchorMetrics.right : viewportOffset.right;
  let previousBottom = anchorMetrics?.visible ? anchorMetrics.bottom : viewportOffset.bottom;
  let previousWidth = anchorMetrics?.visible ? anchorMetrics.width : 0;
  let previousHeight = anchorMetrics?.visible ? anchorMetrics.height : 0;

  visibleWidgetIds.forEach((widgetId, index) => {
    const size = getManagedWidgetSize(widgetId);

    if (!anchorMetrics?.visible && index === 0) {
      placements.set(widgetId, {
        right: viewportOffset.right,
        bottom: viewportOffset.bottom,
      });
      previousRight = viewportOffset.right;
      previousBottom = viewportOffset.bottom;
      previousWidth = size.width;
      previousHeight = size.height;
      return;
    }

    if (laneMode === "stack") {
      const right = anchorMetrics?.visible ? anchorMetrics.right : previousRight;
      const bottom = previousBottom + previousHeight + FLOATING_CONTROL_STACK_GAP;

      placements.set(widgetId, { right, bottom });
      previousRight = right;
      previousBottom = bottom;
      previousWidth = size.width;
      previousHeight = size.height;
      return;
    }

    const right = previousRight + previousWidth + FLOATING_CONTROL_ROW_GAP;
    const anchorBottom = anchorMetrics?.bottom ?? viewportOffset.bottom;
    const anchorHeight = anchorMetrics?.height ?? size.height;
    const bottom = anchorBottom + Math.max(0, (anchorHeight - size.height) / 2);

    placements.set(widgetId, { right, bottom });
    previousRight = right;
    previousBottom = bottom;
    previousWidth = size.width;
    previousHeight = size.height;
  });

  return placements;
}

function getOccupiedRectangles(
  anchorMetrics: FloatingWidgetMetrics | null,
  widgetIds: ManagedFloatingWidgetId[],
  options: FloatingWidgetLayoutOptions = {},
) {
  const rectangles: Array<{ bottom: number; height: number; right: number; width: number }> = [];
  const placements = getManagedWidgetPlacements(anchorMetrics, widgetIds, options);

  if (anchorMetrics?.visible) {
    rectangles.push({
      bottom: anchorMetrics.bottom,
      height: anchorMetrics.height,
      right: anchorMetrics.right,
      width: anchorMetrics.width,
    });
  }

  placements.forEach((placement, widgetId) => {
    const size = getManagedWidgetSize(widgetId);
    rectangles.push({
      bottom: placement.bottom,
      height: size.height,
      right: placement.right,
      width: size.width,
    });
  });

  return rectangles;
}

export function setFloatingWidgetMetrics(
  widgetId: string,
  metrics: {
    bottom: number;
    height: number;
    right: number;
    visible: boolean;
    width: number;
  },
) {
  widgetMetrics.set(widgetId, normalizeMetrics(metrics));
  emitChange();
}

export function clearFloatingWidgetMetrics(widgetId: string) {
  widgetMetrics.delete(widgetId);
  emitChange();
}

export function useFloatingWidgetMetrics(widgetId: string) {
  return useSyncExternalStore(subscribe, () => getWidgetMetrics(widgetId), () => getWidgetMetrics(widgetId));
}

export function setSentryFeedbackTriggerMetrics(metrics: {
  bottom: number;
  height: number;
  right: number;
  visible: boolean;
  width: number;
}) {
  setFloatingWidgetMetrics(SENTRY_WIDGET_ID, metrics);
}

export function clearSentryFeedbackTriggerMetrics() {
  clearFloatingWidgetMetrics(SENTRY_WIDGET_ID);
}

export function useSentryFeedbackTriggerMetrics() {
  return useFloatingWidgetMetrics(SENTRY_WIDGET_ID);
}

export function getManagedFloatingWidgetPlacement(
  anchorMetrics: FloatingWidgetMetrics | null,
  widgetIds: ManagedFloatingWidgetId[],
  targetWidgetId: ManagedFloatingWidgetId,
  options: FloatingWidgetLayoutOptions = {},
): FloatingWidgetPlacement {
  const placements = getManagedWidgetPlacements(anchorMetrics, widgetIds, options);

  return placements.get(targetWidgetId) ?? {
    right: options.viewportOffset?.right ?? DEFAULT_WIDGET_MARGIN,
    bottom: options.viewportOffset?.bottom ?? DEFAULT_WIDGET_MARGIN,
  };
}

export function useManagedFloatingWidgetPlacement(
  targetWidgetId: ManagedFloatingWidgetId,
  widgetIds: ManagedFloatingWidgetId[],
): FloatingWidgetPlacement {
  const anchorMetrics = useSentryFeedbackTriggerMetrics();
  const viewportOffset = useFloatingWidgetViewportOffset();

  return useMemo(
    () => getManagedFloatingWidgetPlacement(anchorMetrics, widgetIds, targetWidgetId, { viewportOffset }),
    [anchorMetrics, targetWidgetId, viewportOffset, widgetIds],
  );
}

export function getFloatingWidgetClearance(
  anchorMetrics: FloatingWidgetMetrics | null,
  widgetIds: ManagedFloatingWidgetId[] = [],
  options: FloatingWidgetLayoutOptions = {},
): FloatingWidgetClearance {
  const rectangles = getOccupiedRectangles(anchorMetrics, widgetIds, options);

  if (rectangles.length === 0) {
    const viewportOffset = options.viewportOffset ?? getDefaultViewportOffset();

    return {
      bottomOffset: viewportOffset.bottom,
      contentBottomPadding: viewportOffset.bottom + FLOATING_CONTROL_CLEARANCE_GAP,
      rightOffset: viewportOffset.right,
    };
  }

  const bottomExtent = Math.max(...rectangles.map((rectangle) => rectangle.bottom + rectangle.height));
  const rightExtent = Math.max(...rectangles.map((rectangle) => rectangle.right + rectangle.width));

  return {
    bottomOffset: bottomExtent + FLOATING_CONTROL_CLEARANCE_GAP,
    contentBottomPadding: bottomExtent + FLOATING_CONTROL_CLEARANCE_GAP,
    rightOffset: rightExtent + FLOATING_CONTROL_CLEARANCE_GAP,
  };
}

export function useFloatingWidgetClearance(widgetIds: ManagedFloatingWidgetId[] = []) {
  const anchorMetrics = useSentryFeedbackTriggerMetrics();
  const viewportOffset = useFloatingWidgetViewportOffset();

  return useMemo(
    () => getFloatingWidgetClearance(anchorMetrics, widgetIds, { viewportOffset }),
    [anchorMetrics, viewportOffset, widgetIds],
  );
}
